// ===========================================================================
// Emoji: the compose picker, and the fixed reaction set.
//
// Two different things, deliberately kept apart:
//
//   * PICKER emoji are typed into the message box. They are ordinary text and
//     go through the text moderator like any other characters, so the list can
//     be as long as is useful.
//   * REACTION emoji reach the other person WITHOUT passing the moderator, so
//     that set is small, fixed, and defined in the shared protocol where the
//     server validates against it.
//
// Emoji are written as CODE POINTS, not literals, to keep this source ASCII.
// ===========================================================================

import { t } from '../i18n.js';
import { el } from '../dom.js';
import { REACTION_EMOJI } from '@lumi/shared';

const cp = (...points) => String.fromCodePoint(...points);

// A small, everyday set. Enough to be expressive without turning into a
// keyboard of its own -- anyone who wants the full range has their own.
const GROUPS = [
  {
    name: 'Smileys',
    points: [
      [0x1f600], [0x1f603], [0x1f604], [0x1f601], [0x1f606], [0x1f605], [0x1f923], [0x1f602],
      [0x1f642], [0x1f643], [0x1f609], [0x1f60a], [0x1f607], [0x1f970], [0x1f60d], [0x1f618],
      [0x1f617], [0x1f61a], [0x1f60b], [0x1f61b], [0x1f92a], [0x1f61c], [0x1f914], [0x1f910],
      [0x1f610], [0x1f611], [0x1f636], [0x1f60f], [0x1f612], [0x1f644], [0x1f62c], [0x1f925],
    ],
  },
  {
    name: 'Feelings',
    points: [
      [0x1f622], [0x1f62d], [0x1f624], [0x1f620], [0x1f621], [0x1f92c], [0x1f92f], [0x1f633],
      [0x1f97a], [0x1f628], [0x1f630], [0x1f625], [0x1f613], [0x1f917], [0x1f914], [0x1f971],
      [0x1f634], [0x1f912], [0x1f915], [0x1f92e], [0x1f927], [0x1f975], [0x1f976], [0x1f60e],
    ],
  },
  {
    name: 'People',
    points: [
      [0x1f44d], [0x1f44e], [0x1f44f], [0x1f64c], [0x1f64f], [0x1f91d], [0x1f4aa], [0x270c, 0xfe0f],
      [0x1f44b], [0x1f596], [0x1f918], [0x1f91f], [0x1f440], [0x1f9e0], [0x1f5e3, 0xfe0f], [0x1f464],
    ],
  },
  {
    name: 'Things',
    points: [
      [0x2764, 0xfe0f], [0x1f9e1], [0x1f49b], [0x1f49a], [0x1f499], [0x1f49c], [0x1f494], [0x2728],
      [0x1f525], [0x1f4af], [0x1f389], [0x1f381], [0x1f3b5], [0x1f3ae], [0x2615], [0x1f355],
      [0x1f436], [0x1f431], [0x1f338], [0x1f30d], [0x2600, 0xfe0f], [0x1f319], [0x26a1], [0x1f308],
    ],
  },
];

/**
 * A popover of emoji that inserts into a text input.
 *
 * @param {HTMLInputElement} input the message box to type into
 * @returns {{ button: HTMLElement, panel: HTMLElement, close: Function }}
 */
export function createEmojiPicker(input) {
  const panel = el('div', { class: 'emoji-panel hidden' });

  for (const group of GROUPS) {
    panel.append(el('div', { class: 'emoji-group-name', text: group.name }));
    panel.append(
      el(
        'div',
        { class: 'emoji-grid' },
        group.points.map((points) =>
          el('button', {
            type: 'button',
            class: 'emoji-btn',
            text: cp(...points),
            // Insert AT THE CARET rather than appending, so picking an emoji
            // mid-sentence does what it looks like it will do.
            onClick: () => insertAtCaret(input, cp(...points)),
          }),
        ),
      ),
    );
  }

  const close = () => panel.classList.add('hidden');
  const button = el('button', {
    type: 'button',
    class: 'btn btn-ghost btn-sm emoji-toggle',
    title: t('Insert an emoji'),
    'aria-label': 'Insert an emoji',
    text: cp(0x1f642),
    onClick: (e) => {
      e.stopPropagation();
      panel.classList.toggle('hidden');
      if (!panel.classList.contains('hidden')) input.focus();
    },
  });

  // Clicking anywhere else puts it away, the way a menu should behave.
  document.addEventListener('click', (e) => {
    if (!panel.contains(e.target) && e.target !== button) close();
  });

  return { button, panel, close };
}

function insertAtCaret(input, text) {
  const start = input.selectionStart ?? input.value.length;
  const end = input.selectionEnd ?? input.value.length;
  input.value = input.value.slice(0, start) + text + input.value.slice(end);
  const caret = start + text.length;
  input.setSelectionRange(caret, caret);
  input.focus();
  // Let anything listening (typing indicator, send-button state) notice.
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * The row of reaction choices shown when someone reacts to a message.
 * @param {(emoji: string) => void} onPick
 */
export function createReactionBar(onPick) {
  return el(
    'div',
    { class: 'react-bar' },
    REACTION_EMOJI.map((emoji) =>
      el('button', {
        type: 'button',
        class: 'react-choice',
        text: emoji,
        title: `React ${emoji}`,
        onClick: (e) => {
          e.stopPropagation();
          onPick(emoji);
        },
      }),
    ),
  );
}

export default { createEmojiPicker, createReactionBar };
